import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Eye,
  FileText,
  Info,
  Pencil,
  Plus,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { KpiCard, KpiGrid } from "../components/KpiCard";
import { FilterCard } from "../components/FilterCard";
import { FormField, selectClass } from "../components/FormField";
import { StatusBadge } from "../components/StatusBadge";
import { TableToolbar } from "../components/TableToolbar";
import {
  authorizationRequestStatusOptions,
  authorizationRequestUrgencyOptions,
} from "../data";
import {
  fetchAuditorProforma,
  fetchHospitalProforma,
} from "../api/proformaApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { useProformas } from "../hooks/useProformas";
import { cn } from "@/shared/lib/cn";
import { ProformaDetailModal } from "../components/ProformaDetailModal";
import { DueCountdown } from "../components/DueCountdown";
import type { AuthorizationRequest } from "../types";

type SortKey = keyof Pick<
  AuthorizationRequest,
  "id" | "patient" | "insurer" | "insurancePackage" | "hospital" | "company" | "status" | "dueAt"
>;

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function AuthorizationRequestsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.requests;
  const { exportTable } = usePageActions();
  const accountType = getStoredAccountType();
  const isAuditor = accountType === "auditor";
  // Compte Assurance : saisit les demandes envoyées par l'hôpital (Admin en appui) ; l'Audit les valide.
  const isDesk = accountType === "admin" || accountType === "enterprise";
  const isHospital = accountType === "hospital";
  const canCreate = isDesk;
  const showActionsColumn = isAuditor || isDesk;
  const pageTitle = accountType === "auditor" ? t.safecheck.nav.requestsToValidate : labels.title;
  // Compte Assurance : ses demandes sont toutes chez lui, la colonne Assureur n'apporte rien.
  const showInsurer = accountType !== "enterprise";

  const [toolbarSearch, setToolbarSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [detailId, setDetailId] = useState<number | null>(null);
  const detailFetcher = isHospital ? fetchHospitalProforma : fetchAuditorProforma;

  // Statut choisi depuis une carte du tableau de bord (?status=pending, approved, rejected).
  const [searchParams] = useSearchParams();
  const [draftStatus, setDraftStatus] = useState(() => searchParams.get("status") ?? "");
  const [draftUrgency, setDraftUrgency] = useState(() => searchParams.get("urgency") ?? "");
  const [appliedStatus, setAppliedStatus] = useState(() => searchParams.get("status") ?? "");
  const [appliedUrgency, setAppliedUrgency] = useState(() => searchParams.get("urgency") ?? "");
  // Filtres par assureur, hôpital et entreprise (valeurs présentes dans la liste).
  const [draftPlace, setDraftPlace] = useState({ insurer: "", hospital: "", company: "" });
  const [appliedPlace, setAppliedPlace] = useState({ insurer: "", hospital: "", company: "" });

  const { requests, summary, loading, error, usingApi, reload } = useProformas();

  const applyFilters = () => {
    setAppliedStatus(draftStatus);
    setAppliedUrgency(draftUrgency);
    setAppliedPlace(draftPlace);
    setPage(1);
  };

  const resetFilters = () => {
    setToolbarSearch("");
    setDraftStatus("");
    setDraftUrgency("");
    setAppliedStatus("");
    setAppliedUrgency("");
    setDraftPlace({ insurer: "", hospital: "", company: "" });
    setAppliedPlace({ insurer: "", hospital: "", company: "" });
    setPage(1);
  };

  const filtered = useMemo(() => {
    return requests.filter((request) => {
      const haystack =
        `${request.id} ${request.patient} ${request.patientId} ${request.insurer ?? ""} ${request.insurancePackage ?? ""} ${request.packageCode} ${request.packageRef} ${request.hospital} ${request.company} ${request.date}`.toLowerCase();

      const byToolbarSearch = toolbarSearch ? haystack.includes(toolbarSearch.toLowerCase()) : true;
      const byStatus = appliedStatus ? request.status === appliedStatus : true;
      const byUrgency = appliedUrgency ? request.urgency === appliedUrgency : true;
      const byPlace =
        (!appliedPlace.insurer || request.insurer === appliedPlace.insurer) &&
        (!appliedPlace.hospital || request.hospital === appliedPlace.hospital) &&
        (!appliedPlace.company || request.company === appliedPlace.company);

      return byToolbarSearch && byStatus && byUrgency && byPlace;
    });
  }, [requests, toolbarSearch, appliedStatus, appliedUrgency, appliedPlace]);

  const choices = useMemo(() => {
    const unique = (values: Array<string | undefined>) =>
      [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b));
    return {
      insurer: unique(requests.map((r) => r.insurer)),
      hospital: unique(requests.map((r) => r.hospital)),
      company: unique(requests.map((r) => r.company)),
    };
  }, [requests]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const left = a[sortKey] ?? "";
      const right = b[sortKey] ?? "";
      const cmp = String(left).localeCompare(String(right), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const handleExportCsv = () => {
    exportTable(
      "demandes-autorisation.csv",
      ["ID", "Patient", ...(showInsurer ? ["Assureur"] : []), "Package", "Hôpital", "Entreprise", "Statut", "Urgence", "Date"],
      sorted.map((request) => [
        request.id,
        request.patient,
        ...(showInsurer ? [request.insurer ?? ""] : []),
        request.insurancePackage || request.packageCode,
        request.hospital,
        request.company,
        labels.status[request.status],
        labels.urgency[request.urgency],
        request.date,
      ]),
    );
  };

  const amounts = useMemo(() => {
    const sum = (list: AuthorizationRequest[], pick: (r: AuthorizationRequest) => number) => list.reduce((n, r) => n + pick(r), 0);
    const requested = (r: AuthorizationRequest) => r.estimatedAmount ?? 0;
    const byStatus = (status: AuthorizationRequest["status"]) => requests.filter((r) => r.status === status);
    return {
      total: sum(requests, requested),
      pending: sum(byStatus("pending"), requested),
      approvedRequested: sum(byStatus("approved"), requested),
      approvedValidated: sum(byStatus("approved"), (r) => r.approvedAmount ?? requested(r)),
      rejected: sum(byStatus("rejected"), requested),
    };
  }, [requests]);
  const usd = (value: number) => `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;

  const summaryCards = [
    {
      id: "total" as const,
      label: labels.summary.total,
      value: summary.total,
      sub: `${usd(amounts.total)} demandés`,
      icon: FileText,
      tone: "purple" as const,
      filterStatus: "",
    },
    {
      id: "pending" as const,
      label: labels.summary.pending,
      value: summary.pending,
      sub: `${usd(amounts.pending)} en attente`,
      icon: Clock,
      tone: "amber" as const,
      filterStatus: "pending",
    },
    {
      id: "approved" as const,
      label: labels.summary.approved,
      value: summary.approved,
      sub: `${usd(amounts.approvedValidated)} validés sur ${usd(amounts.approvedRequested)}`,
      icon: CheckCircle,
      tone: "mint" as const,
      filterStatus: "approved",
    },
    {
      id: "rejected" as const,
      label: labels.summary.rejected,
      value: summary.rejected,
      sub: `${usd(amounts.rejected)} rejetés`,
      icon: XCircle,
      tone: "red" as const,
      filterStatus: "rejected",
    },
  ];

  const columns: { key: SortKey; label: string }[] = [
    { key: "id", label: labels.columns.id },
    { key: "patient", label: labels.columns.patient },
    ...(showInsurer ? [{ key: "insurer" as SortKey, label: "Assureur" }] : []),
    { key: "insurancePackage", label: labels.columns.package },
    { key: "hospital", label: labels.columns.hospital },
    { key: "company", label: labels.columns.company },
    { key: "status", label: labels.fields.status },
    { key: "dueAt", label: "Délai restant" },
  ];

  const colSpan = columns.length + (showActionsColumn ? 1 : 0);

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={
          accountType === "auditor"
            ? [{ label: t.safecheck.nav.proforma }, { label: pageTitle }]
            : [{ label: pageTitle }]
        }
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">{pageTitle}</h1>
        {canCreate && (
          <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/dashboard/requests/refactions"
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors duration-150 hover:border-accent hover:bg-accent/20"
          >
            <FileText size={16} />
            Voir la liste des réfactions
          </Link>
          <Link
            to="/dashboard/requests/create"
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          >
            <Plus size={16} />
            {labels.add}
          </Link>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-danger/20 bg-danger-soft px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <FilterCard title={labels.filtersTitle} onFilter={applyFilters} onReset={resetFilters}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          {(
            [
              ["insurer", "Assureur", "Tous les assureurs"],
              ["hospital", "Hôpital", "Tous les hôpitaux"],
              ["company", "Entreprise", "Toutes les entreprises"],
            ] as const
          ).filter(([key]) => showInsurer || key !== "insurer").map(([key, label, all]) => (
            <FormField key={key} label={label}>
              <select value={draftPlace[key]} onChange={(event) => setDraftPlace({ ...draftPlace, [key]: event.target.value })} className={selectClass}>
                <option value="">{all}</option>
                {choices[key].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </FormField>
          ))}
          <FormField label={labels.fields.status}>
            <select
              value={draftStatus}
              onChange={(event) => setDraftStatus(event.target.value)}
              className={selectClass}
            >
              {authorizationRequestStatusOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allStatuses}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.urgency}>
            <select
              value={draftUrgency}
              onChange={(event) => setDraftUrgency(event.target.value)}
              className={selectClass}
            >
              {authorizationRequestUrgencyOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allUrgencies}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </FilterCard>

      <KpiGrid className="mb-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <KpiCard
            key={card.id}
            tone={card.tone}
            icon={card.icon}
            label={card.label}
            value={card.value}
            hint={card.sub}
            active={appliedStatus === card.filterStatus && card.filterStatus !== ""}
            onClick={() => {
              setDraftStatus(card.filterStatus);
              setAppliedStatus(card.filterStatus);
              setPage(1);
            }}
          />
        ))}
      </KpiGrid>

      <div className="theme-card">
        <TableToolbar
          search={toolbarSearch}
          onSearchChange={(value) => {
            setToolbarSearch(value);
            setPage(1);
          }}
          searchPlaceholder={labels.searchPlaceholder}
          resultCount={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={labels.pagination.entries}
          onExport={handleExportCsv}
          exportLabel={labels.exportCsv}
          onReset={resetFilters}
          resetLabel={t.safecheck.reset}
        />

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-[11px] font-semibold tracking-wide text-muted"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center gap-1 uppercase"
                    >
                      {column.label}
                      <span className="flex flex-col">
                        <ChevronUp
                          size={10}
                          className={cn(
                            "-mb-1",
                            sortKey === column.key && sortDir === "asc" ? "text-primary" : "",
                          )}
                        />
                        <ChevronDown
                          size={10}
                          className={sortKey === column.key && sortDir === "desc" ? "text-primary" : ""}
                        />
                      </span>
                    </button>
                  </th>
                ))}
                {showActionsColumn && (
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-16 text-center text-sm text-muted">
                    Chargement…
                  </td>
                </tr>
              ) : (
                <>
                  {pageData.map((request, index) => (
                    <tr
                      key={request.id}
                      className={cn(
                        "border-b border-border last:border-0 hover:bg-card-muted/30",
                        index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                      )}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        <button
                          type="button"
                          onClick={() => setDetailId(Number(request.id))}
                          className="inline-flex items-center gap-2 text-primary transition-colors hover:underline"
                          title="Voir le détail"
                        >
                          <ChevronRight size={14} className="text-muted" />
                          #{request.id}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-foreground">{request.patient}</p>
                        <p className="text-xs text-muted">{request.patientId}</p>
                      </td>
                      {showInsurer ? <td className="px-4 py-3 text-sm text-foreground">{request.insurer || "—"}</td> : null}
                      <td className="px-4 py-3">
                        <StatusBadge
                          variant="purple"
                          className="max-w-xs whitespace-normal text-left leading-snug"
                        >
                          {request.insurancePackage || (request.packageCode ? `${request.packageCode} / ${request.packageRef}` : "—")}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">{request.hospital}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{request.company}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          variant={
                            request.status === "approved"
                              ? "success"
                              : request.status === "rejected"
                                ? "danger"
                                : "warning"
                          }
                        >
                          {labels.status[request.status]}
                        </StatusBadge>
                        {request.chiefOnly ? <p className="mt-1 text-xs font-medium text-red-700">Médecin en chef</p> : null}
                        {request.status === "pending" && request.holdReason ? (
                          <p className="mt-1 max-w-[14rem] text-xs text-amber-700" title={request.holdReason}>
                            Mise en attente : {request.holdReason}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        {request.status === "pending" ? <DueCountdown dueAt={request.dueAt} /> : <span className="text-xs text-muted">Traitée</span>}
                        <span className="mt-1 flex items-center gap-1 text-xs text-muted">
                          <Info size={11} />
                          {labels.urgency[request.urgency]} · {request.date}
                        </span>
                      </td>
                      {showActionsColumn && (
                        <td className="px-4 py-3">
                          {isAuditor ? (
                            <button
                              type="button"
                              onClick={() => setDetailId(Number(request.id))}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent hover:text-primary"
                              title="Voir la demande"
                              aria-label="Voir la demande"
                            >
                              <Eye size={16} />
                            </button>
                          ) : isDesk ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setDetailId(Number(request.id))}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent hover:text-primary"
                                title="Voir la demande"
                                aria-label="Voir la demande"
                              >
                                <Eye size={16} />
                              </button>
                              {request.status === "pending" && (
                                <Link
                                  to={`/dashboard/requests/${request.id}/edit`}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-primary/30 bg-primary-soft text-primary transition-colors hover:bg-accent/20 hover:border-accent"
                                  title="Modifier"
                                  aria-label="Modifier"
                                >
                                  <Pencil size={16} />
                                </Link>
                              )}
                            </div>
                          ) : (
                            <StatusBadge
                              variant={request.status === "approved" ? "success" : "danger"}
                            >
                              {labels.status[request.status]}
                            </StatusBadge>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {pageData.length === 0 && (
                    <tr>
                      <td colSpan={colSpan} className="px-4 py-16 text-center text-sm text-muted">
                        {labels.empty}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing} {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of} {sorted.length}{" "}
            {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded-lg border border-border px-3 py-1.5 transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {labels.pagination.previous}
            </button>
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="rounded-lg border border-border px-3 py-1.5 transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>

      {detailId !== null && (
        <ProformaDetailModal
          proformaId={detailId}
          fallbackTitle={`Demande #${detailId}`}
          canFetch={Boolean(getAuthToken()) && usingApi}
          fetcher={detailFetcher}
          allowValidation={isAuditor}
          hideActCodes={accountType === "enterprise"}
          onValidated={() => void reload()}
          onClose={() => setDetailId(null)}
        />
      )}
    </>
  );
}
